import React, { useState } from 'react';
import { HelpCircle, ChevronDown, User, Briefcase, Bike, Mail, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface FAQItemProps {
  question: string;
  answer: string;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-gray-100 rounded-2xl mb-3 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <button
        className="w-full px-6 py-4 flex items-center justify-between text-left focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-bold text-gray-900 pr-4">{question}</span>
        <ChevronDown
          className={`text-[#FF6B35] transition-transform duration-300 shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          size={20}
        />
      </button>
      <div
        className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 pb-4 opacity-100' : 'max-h-0 opacity-0'
          }`}
      >
        <p className="text-gray-600 leading-relaxed text-sm">{answer}</p>
      </div>
    </div>
  );
};

const HelpSupport: React.FC = () => {
  const { i18n } = useTranslation();
  const isEng = i18n.language.startsWith('en');

  const faqs = {
    passengers: {
      icon: <User className="text-[#FF6B35] w-6 h-6" />,
      title: isEng ? "For Passengers" : "Para Passageiros",
      items: [
        {
          q: isEng ? "How to request a moto?" : "Como pedir uma mota?",
          a: isEng
            ? "Open the app, set your destination, confirm the price, and tap 'Request Moto'. A nearby driver will be assigned to you."
            : "Abra a aplicação MotoRush, defina o seu destino e o local de partida. Confirme a tarifa apresentada no ecrã e toque em 'Pedir Mota'. O sistema irá alocar o motorista mais próximo de si."
        },
        {
          q: isEng ? "What if I forgot an item?" : "O que fazer se esqueci de um item na mota?",
          a: isEng
            ? "Go to 'Trip History', select the trip, and use the 'Contact Driver' option. If unsuccessful, contact our WhatsApp support."
            : "Vá ao separador 'Histórico de Viagens', seleccione a viagem em questão e use o botão 'Contactar Motorista' (disponível até 24h após a viagem). Caso não consiga, entre em contacto imediato com a nossa linha de Suporte via WhatsApp."
        },
        {
          q: isEng ? "How does Loyalty Tier work?" : "Como funcionam os pontos de fidelidade (Loyalty Tier)?",
          a: isEng
            ? "You earn points for every kilometer traveled. Points can be redeemed for discounts on future rides."
            : "A cada quilómetro percorrido, ganha pontos automaticamente na sua conta. Quando atingir determinados patamares (Silver, Gold, Platinum), poderá trocar esses pontos por descontos nas próximas viagens ou envios gratuitos."
        },
        {
          q: isEng ? "How to report a problem?" : "Como reportar um problema com a viagem?",
          a: isEng
            ? "Rate the driver 1-3 stars to trigger the problem reporting flow, or contact support directly."
            : "No final da viagem, ao avaliar o motorista com 3 estrelas ou menos, ser-lhe-á automaticamente pedido para detalhar o problema. Alternativamente, use a opção 'Ajuda' no menu principal da app."
        }
      ]
    },
    drivers: {
      icon: <Bike className="text-blue-500 w-6 h-6" />,
      title: isEng ? "For Drivers" : "Para Motoristas",
      items: [
        {
          q: isEng ? "How do I get paid?" : "Como recebo o pagamento das minhas viagens?",
          a: isEng
            ? "Payments via cash are yours to keep. App payments accumulate in your wallet and can be withdrawn to your bank account."
            : "As viagens pagas em dinheiro físico pelo passageiro ficam imediatamente consigo. Viagens pagas via saldo da plataforma são acumuladas na sua carteira (Wallet) na App. Pode solicitar o levantamento para o seu IBAN a qualquer momento."
        },
        {
          q: isEng ? "How are earnings calculated?" : "Como funciona o cálculo de ganhos por quilómetro?",
          a: isEng
            ? "Earnings are based on base fare + per kilometer rate + time factor."
            : "Os seus ganhos são transparentes. O cálculo inclui uma tarifa base de arranque, somada a um valor fixo por cada quilómetro percorrido e um fctor de tempo, garantindo que mesmo no trânsito lento de Luanda o seu tempo é remunerado."
        },
        {
          q: isEng ? "What if the passenger doesn't show up?" : "O que fazer se o passageiro não aparecer no local de recolha?",
          a: isEng
            ? "Wait 5 minutes, try calling, then you can cancel the trip without penalty."
            : "Ao chegar ao local de recolha, o cronómetro inicia. Após aguardar 5 minutos e tentar contactar o passageiro, poderá cancelar a viagem seleccionando a opção 'Passageiro não apareceu'. Não será penalizado e receberá a taxa de cancelamento aplicável."
        }
      ]
    },
    b2b: {
      icon: <Briefcase className="text-purple-500 w-6 h-6" />,
      title: isEng ? "For Corporate Clients (B2B)" : "Para Clientes Corporativos (B2B)",
      items: [
        {
          q: isEng ? "How to add employees?" : "Como adicionar novos funcionários à minha conta corporativa?",
          a: isEng
            ? "Access the web dashboard and use the 'Employees' tab to invite via email."
            : "Aceda ao Portal Web da MotoRush com as credenciais de Gestor. Vá ao separador 'Colaboradores' e insira os números de telefone ou emails dos seus funcionários para lhes dar acesso à conta da empresa."
        },
        {
          q: isEng ? "How to access invoices?" : "Como aceder às facturas mensais e histórico de rotas da empresa?",
          a: isEng
            ? "Invoices and tracking reports are available in the 'Billing' tab of your corporate dashboard."
            : "Todas as facturas são geradas automaticamente no último dia do mês e podem ser descarregadas em PDF/Excel no separador 'Facturação' do seu painel web. O histórico e o mapa das rotas estão na secção 'Relatórios'."
        }
      ]
    }
  };

  return (
    <div className="pt-24 pb-16 bg-gray-50 min-h-screen animate-fadeIn">
      <div className="container mx-auto px-6 max-w-4xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-3 bg-gray-200 rounded-2xl mb-4 text-gray-700">
            <HelpCircle size={32} />
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight">
            {isEng ? "Help & Support" : "Central de Ajuda / FAQ"}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {isEng
              ? "Find quick answers to common questions."
              : "Encontre respostas rápidas para as dúvidas mais comuns. Poupamos-lhe tempo para que possa continuar em movimento."}
          </p>
        </div>

        <div className="space-y-12">
          {/* FAQ Sections */}
          {[faqs.passengers, faqs.drivers, faqs.b2b].map((category, idx) => (
            <div key={idx} className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8">
              <div className="flex items-center gap-3 mb-6 border-b border-gray-100 pb-4">
                <div className="p-2 bg-gray-50 rounded-lg">
                  {category.icon}
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{category.title}</h2>
              </div>
              <div className="space-y-1">
                {category.items.map((item, i) => (
                  <FAQItem key={i} question={item.q} answer={item.a} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Contact Support Channels */}
        <div className="mt-12 bg-gradient-to-br from-[#FF6B35] to-[#4A90E2] rounded-3xl shadow-xl p-8 md:p-12 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">
            {isEng ? "Still need help?" : "Ainda precisa de ajuda?"}
          </h3>
          <p className="mb-8 text-white/90 max-w-lg mx-auto">
            {isEng
              ? "Our official support channels are available."
              : "A nossa equipa oficial de suporte ao cliente em Luanda está pronta para ajudar."}
          </p>

          <div className="flex flex-col md:flex-row justify-center items-center gap-6">
            <a
              href="mailto:suporte@motorush.ao"
              className="flex items-center gap-3 bg-white/20 hover:bg-white/30 backdrop-blur-md px-6 py-4 rounded-2xl transition-all w-full md:w-auto"
            >
              <Mail size={24} />
              <div className="text-left">
                <div className="text-sm text-white/70">{isEng ? "Official Email" : "Email Oficial"}</div>
                <div className="font-bold">suporte@motorush.ao</div>
              </div>
            </a>

            <a
              href="https://wa.me/244954009112"
              target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-3 bg-[#25D366] hover:bg-[#20bd5a] px-6 py-4 rounded-2xl transition-all shadow-lg w-full md:w-auto transform hover:-translate-y-1"
            >
              <MessageCircle size={24} />
              <div className="text-left">
                <div className="text-sm text-green-100">{isEng ? "WhatsApp Support" : "Suporte via WhatsApp"}</div>
                <div className="font-bold">+244 954 009 112</div>
              </div>
            </a>
          </div>
        </div>

      </div>
    </div>
  );
};

export default HelpSupport;
